// Database types are defined in schema.ts, not needed for basic client
import { UserProfile } from '@shared/types';
import { apiRequest } from './fetchWithAuth';

// NOTE: Frontend must not import '@supabase/supabase-js' directly.
// All data access goes through our API using apiRequest below.

/**
 * Login user via API (not Supabase Auth)
 * Cookies are set server-side for security
 */
export async function loginUser(email: string, password: string): Promise<{
  user: UserProfile | null;
  error: string | null;
  requiresVerification?: boolean;
  verificationEmail?: string;
}> {
  try {
    console.log('🔐 Attempting login for:', email);

    // Deliberately use raw fetch here so we can read structured non-2xx payloads
    // (e.g., verificationRequired) instead of throwing through the wrapper.
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      redirect: 'error',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const responseData = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      return {
        user: null,
        error: responseData?.message || `HTTP ${response.status}`,
        requiresVerification: Boolean(responseData?.verificationRequired),
        verificationEmail: responseData?.email || email
      };
    }

    if (responseData.user) {
      console.log('✅ Login successful');
      // No localStorage needed - cookies handle auth
      return { user: responseData.user, error: null };
    }

    return { user: null, error: 'Login failed' };
  } catch (error) {
    console.error('❌ Login error:', error);
    return {
      user: null,
      error: error instanceof Error ? error.message : 'Login failed'
    };
  }
}

export async function verifyEmailCode(email: string, code: string): Promise<{ success: boolean; error: string | null }> {
  try {
    await apiRequest('/api/auth/verify-email-code', {
      method: 'POST',
      data: { email, code }
    });
    return { success: true, error: null };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to verify code'
    };
  }
}

export async function resendVerificationCode(email: string): Promise<{ success: boolean; error: string | null }> {
  try {
    await apiRequest('/api/auth/resend-verification-code', {
      method: 'POST',
      data: { email }
    });
    return { success: true, error: null };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to resend code'
    };
  }
}

/**
 * Get current user from API
 * Uses cookies for authentication
 */
export async function getCurrentUser(): Promise<UserProfile | null> {
  try {
    const response = await apiRequest<{ user: UserProfile }>('/api/auth/me', {
      method: 'GET'
    });
    
    return response.user || null;
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
}

/**
 * Logout user
 * Clears cookies server-side
 */
export async function logoutUser(): Promise<void> {
  try {
    await apiRequest('/api/auth/logout', {
      method: 'POST'
    });
    
    console.log('✅ Logged out successfully');
  } catch (error) {
    console.error('❌ Logout error:', error);
  }
}

// Re-export auth functions for backward compatibility
export const auth = {
  login: loginUser,
  logout: logoutUser,
  getCurrentUser
};
